import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Maximize2, Wifi, WifiOff } from "lucide-react";
import { FaseBadge } from "@/components/FaseBadge";
import { CompetitionLogo } from "@/components/CompetitionLogo";

const POLL_MS = 2000;
const GOLD = "#C9A84C";

/**
 * Círculo com foto do atleta (fallback: iniciais do nome sobre fundo dourado escuro).
 */
function AthleteAvatar({ athlete, size = 48, highlight = false }) {
  const initials = (athlete?.nome || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  const src = athlete?.foto_url
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${athlete.foto_url}`
    : null;
  const borderColor = highlight ? GOLD : "rgba(201,168,76,0.35)";
  const style = {
    width: size,
    height: size,
    borderRadius: "9999px",
    border: `2px solid ${borderColor}`,
    background: "#111",
    color: GOLD,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    fontFamily: "var(--font-display, system-ui)",
    fontWeight: 900,
    fontSize: Math.max(11, Math.floor(size * 0.34)),
    letterSpacing: "0.05em",
  };
  return (
    <div style={style} data-testid={`backstage-avatar-${athlete?.id}`}>
      {src ? (
        <img
          src={src}
          alt={athlete?.nome || "atleta"}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

/**
 * Monitor de Backstage.
 * - Sincroniza automaticamente com o Painel do Maestro via polling (a cada 2s).
 * - Layout tipo TV: fundo preto profundo, textos gigantes, ouro para destaque.
 * - Utiliza endpoint público para permitir modo kiosk sem login.
 */
export default function Backstage() {
  const { currentId } = useCompetition();
  const [searchParams] = useSearchParams();
  const { competitionId: pathCid } = useParams();
  const forcedCid = searchParams.get("competition_id") || pathCid;
  const cid = forcedCid || currentId;

  const [snap, setSnap] = useState(null);
  const [online, setOnline] = useState(true);
  const [clock, setClock] = useState(new Date());
  const containerRef = useRef(null);

  const fetchSnap = useCallback(async () => {
    if (!cid) return;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/stage-state?competition_id=${cid}`;
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSnap(data);
      setOnline(true);
    } catch (e) {
      console.warn("Backstage poll error:", e?.message);
      setOnline(false);
    }
  }, [cid]);

  useEffect(() => {
    fetchSnap();
    const t = setInterval(fetchSnap, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSnap]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const clockStr = useMemo(
    () =>
      clock.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    [clock]
  );

  if (!cid) {
    return (
      <div className="min-h-screen text-white p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-4xl font-black tracking-tight">
            MONITOR BACKSTAGE
          </h1>
          <CompetitionSelector />
        </div>
        <div className="card p-6 text-center text-[var(--text-secondary)]">
          Selecione uma competição para iniciar o monitor.
        </div>
      </div>
    );
  }

  const current = snap?.current;
  const nxt = snap?.next;
  const upcoming = snap?.upcoming || [];

  return (
    <div
      ref={containerRef}
      data-testid="backstage-page"
      className="min-h-screen w-full text-white overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0A0A0A 0%, #111111 100%)",
        fontFeatureSettings: '"tnum"',
      }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-8 py-4 border-b"
        style={{ borderColor: "rgba(201,168,76,0.25)" }}
      >
        <div className="flex items-center gap-3">
          <img
            src="/logo.jpg"
            alt="FITS4YOU"
            className="w-10 h-10 object-contain bg-white p-1 rounded-sm"
          />
          <div>
            <div
              className="font-display font-black tracking-widest text-lg"
              style={{ color: GOLD }}
            >
              FITS4YOU EVENTS
            </div>
            <div
              className="text-[10px] uppercase tracking-[0.3em]"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              Monitor de Backstage
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4">
            <CompetitionLogo
              competition={snap?.competition}
              height={48}
              testId="backstage-comp-logo"
              fallback={
                <div className="text-xs uppercase tracking-widest text-white/60 text-right" data-testid="backstage-comp-name">
                  {snap?.competition?.nome || "—"}
                </div>
              }
            />
            <div
              className="font-display font-black text-3xl tabular-nums"
              style={{ color: GOLD }}
              data-testid="backstage-clock"
            >
              {clockStr}
            </div>
          </div>
          <div
            className="flex items-center gap-2 text-xs"
            data-testid="backstage-status"
            title={online ? "Sincronizado" : "Sem conexão"}
          >
            {online ? (
              <>
                <Wifi size={16} style={{ color: GOLD }} />
                <span
                  className="w-2 h-2 rounded-full inline-block animate-pulse"
                  style={{ background: GOLD }}
                />
              </>
            ) : (
              <WifiOff size={16} className="text-red-500" />
            )}
          </div>
          <button
            data-testid="backstage-fullscreen"
            onClick={goFullscreen}
            className="btn-ghost flex items-center gap-2"
            title="Tela cheia"
          >
            <Maximize2 size={16} />
          </button>
          <div className="hidden md:block">
            <CompetitionSelector hideEmpty />
          </div>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 px-8 py-6">
        {/* NO PALCO AGORA — 60% */}
        <div
          className="lg:col-span-3 rounded-sm p-8 flex flex-col justify-between"
          style={{
            background: "linear-gradient(180deg, rgba(201,168,76,0.12) 0%, rgba(0,0,0,0) 55%)",
            border: `2px solid ${GOLD}`,
            minHeight: "62vh",
          }}
          data-testid="backstage-current"
        >
          <div className="flex items-center justify-between">
            <div
              className="text-xs uppercase tracking-[0.4em] font-bold"
              style={{ color: GOLD }}
            >
              ● No Palco Agora
            </div>
            <div
              className="text-sm tabular-nums text-white/60"
              data-testid="backstage-current-time"
            >
              {current ? `${current.inicio} → ${current.fim}` : "—"}
            </div>
          </div>

          <div className="my-6">
            <div
              className="font-display font-black leading-none tracking-tight break-words"
              style={{
                fontSize: "clamp(3rem, 7vw, 6.5rem)",
              }}
              data-testid="backstage-current-name"
            >
              {current ? current.categoria : "AGUARDANDO INÍCIO"}
            </div>
            {current?.fase && current.fase !== "completo" && (
              <div className="mt-3">
                <FaseBadge fase={current.fase} size="lg" testId="backstage-current-fase" />
              </div>
            )}
            {current && current.fase !== "intervalo" && (
              <div className="mt-4 flex flex-wrap gap-6 text-white/70">
                <div className="text-lg">
                  <span className="text-white/50 text-xs uppercase tracking-widest mr-2">
                    Atletas
                  </span>
                  <span className="tabular-nums font-bold text-white">
                    {current.atletas}
                  </span>
                </div>
                <div className="text-lg">
                  <span className="text-white/50 text-xs uppercase tracking-widest mr-2">
                    Duração
                  </span>
                  <span className="tabular-nums font-bold text-white">
                    {current.duracao_min} min
                  </span>
                </div>
                <div className="text-lg">
                  <span className="text-white/50 text-xs uppercase tracking-widest mr-2">
                    Ordem
                  </span>
                  <span className="tabular-nums font-bold text-white">
                    #{current.ordem}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Athletes list */}
          {current?.athletes?.length > 0 && (
            <div
              className="border-t pt-4"
              style={{ borderColor: "rgba(201,168,76,0.25)" }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.4em] mb-3 font-bold"
                style={{ color: GOLD }}
              >
                Ordem de Chamada
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2">
                {current.athletes.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-3"
                    data-testid={`backstage-current-athlete-${a.id}`}
                    style={{ opacity: a.wo ? 0.5 : 1 }}
                  >
                    <AthleteAvatar athlete={a} size={40} />
                    <span
                      className="tabular-nums font-display font-black w-10 text-right"
                      style={{ color: GOLD, fontSize: "1.35rem" }}
                    >
                      {a.numero}
                    </span>
                    <span className="text-lg truncate" style={{ textDecoration: a.wo ? "line-through" : "none" }}>
                      {a.nome}
                    </span>
                    {a.wo ? (
                      <span
                        data-testid={`backstage-wo-${a.id}`}
                        className="text-[10px] font-black tracking-widest px-1.5 py-0.5 shrink-0"
                        style={{ background: "#FF453A", color: "#fff" }}
                      >
                        W.O.
                      </span>
                    ) : !a.has_score ? (
                      <span
                        data-testid={`backstage-noscore-${a.id}`}
                        className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1"
                        style={{ border: "1px solid rgba(255,204,0,0.6)", color: "#FFCC00" }}
                        title="Nenhuma nota lançada nesta categoria"
                      >
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: "#FFCC00", display: "inline-block" }} />
                        SEM NOTA
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
              {current.athletes.some((a) => !a.wo && !a.has_score) && (
                <div
                  data-testid="backstage-noscore-summary"
                  className="mt-3 text-xs tracking-widest uppercase"
                  style={{ color: "#FFCC00" }}
                >
                  {current.athletes.filter((a) => !a.wo && !a.has_score).length} atleta(s) ainda sem nota nesta categoria
                </div>
              )}
            </div>
          )}
        </div>

        {/* PREPARAR (EM ESPERA) — 40% */}
        <div
          className="lg:col-span-2 rounded-sm p-6 flex flex-col"
          style={{
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.02)",
            minHeight: "62vh",
          }}
          data-testid="backstage-next"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.4em] text-white/70 font-bold">
              Preparar · Em Espera
            </div>
            <div
              className="text-sm tabular-nums text-white/60"
              data-testid="backstage-next-time"
            >
              {nxt ? `Início ${nxt.inicio}` : "—"}
            </div>
          </div>
          <div
            className="font-display font-black leading-tight break-words"
            style={{ fontSize: "clamp(2rem, 3.5vw, 3.5rem)" }}
            data-testid="backstage-next-name"
          >
            {nxt ? nxt.categoria : "— Fim do evento —"}
          </div>
          {nxt?.fase && nxt.fase !== "completo" && (
            <div className="mt-2">
              <FaseBadge fase={nxt.fase} testId="backstage-next-fase" />
            </div>
          )}
          {nxt && nxt.fase !== "intervalo" && (
            <div className="mt-2 text-sm text-white/50">
              {nxt.atletas} atletas · {nxt.duracao_min} min
            </div>
          )}

          {nxt?.athletes?.length > 0 && (
            <div className="mt-6 flex-1">
              <div
                className="text-[11px] uppercase tracking-[0.4em] mb-3 font-bold"
                style={{ color: GOLD }}
              >
                Próximos a Entrar
              </div>
              <div className="space-y-3">
                {nxt.athletes.map((a, i) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-4 px-4 py-3 rounded-sm"
                    style={{
                      background:
                        i === 0
                          ? "rgba(201,168,76,0.14)"
                          : "rgba(255,255,255,0.04)",
                      borderLeft: i === 0 ? `3px solid ${GOLD}` : "3px solid transparent",
                    }}
                    data-testid={`backstage-next-athlete-${a.id}`}
                  >
                    <AthleteAvatar athlete={a} size={56} highlight={i === 0} />
                    <span
                      className="tabular-nums font-display font-black w-14 text-center"
                      style={{
                        color: GOLD,
                        fontSize: "2rem",
                      }}
                    >
                      {a.numero}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-xl truncate">{a.nome}</div>
                      {a.equipe && (
                        <div className="text-xs text-white/50 truncate">
                          {a.equipe}
                        </div>
                      )}
                    </div>
                    {i === 0 && (
                      <span
                        className="text-[10px] font-bold tracking-widest px-2 py-1"
                        style={{ background: GOLD, color: "#0a0a0a" }}
                      >
                        PRÓXIMO
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming 5 categories strip */}
      <div className="px-8 pb-8">
        <div
          className="text-[11px] uppercase tracking-[0.4em] mb-3 font-bold"
          style={{ color: GOLD }}
        >
          Roteiro · Próximas Categorias
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {upcoming.length === 0 && (
            <div className="col-span-full text-center text-white/40 py-6 text-sm">
              Sem categorias na fila.
            </div>
          )}
          {upcoming.map((c, i) => (
            <div
              key={c.key || c.category_id || i}
              className="rounded-sm p-4"
              style={{
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.02)",
              }}
              data-testid={`backstage-upcoming-${c.category_id || c.fase}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="text-xs tabular-nums font-black"
                  style={{ color: GOLD }}
                >
                  {c.ordem != null ? `#${c.ordem}` : ""}
                </span>
                <span className="text-[11px] tabular-nums text-white/50">
                  {c.inicio}
                </span>
              </div>
              <div className="text-sm font-bold leading-snug line-clamp-2 min-h-[2.5em]">
                {c.categoria} <FaseBadge fase={c.fase} />
              </div>
              <div className="text-[11px] text-white/40 mt-2 tabular-nums">
                {c.fase === "intervalo" ? `${c.duracao_min}′ de pausa` : `${c.atletas} atletas · ${c.duracao_min}′`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
